// src/store/slices/modelSlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'
import { selectSelectedProjectId } from './projectSlice'

export interface ModelSliceState {
  selectedModelId: string | undefined
  selectedModelsByProject: { [projectId: string]: string }
  isComparing: boolean
  comparingModelId: string | undefined
  compareModelId: string | undefined
}

const COMPARE_MODEL_KEY = 'compareModelId'
const SELECTED_MODELS_KEY = 'selectedModelsByProject'

// Helper function to get stored model selections
const getStoredModelSelections = (): { [projectId: string]: string } => {
  const stored = localStorage.getItem(SELECTED_MODELS_KEY)
  return stored ? JSON.parse(stored) : {}
}

const initialState: ModelSliceState = {
  selectedModelId: undefined,
  selectedModelsByProject: getStoredModelSelections(),
  isComparing: false,
  comparingModelId: undefined,
  compareModelId: localStorage.getItem(COMPARE_MODEL_KEY) || undefined,
}

const modelSlice = createSlice({
  name: 'model',
  initialState,
  reducers: {
    setSelectedModelId: (
      state,
      action: PayloadAction<{ modelId: string | undefined; projectId: string }>
    ) => {
      const { modelId, projectId } = action.payload

      // Update the current selection
      state.selectedModelId = modelId

      if (modelId) {
        // Store in project mapping
        state.selectedModelsByProject = {
          ...state.selectedModelsByProject,
          [projectId]: modelId,
        }
        localStorage.setItem(SELECTED_MODELS_KEY, JSON.stringify(state.selectedModelsByProject))
      }
    },

    clearSelectedModelId: (state, action: PayloadAction<string | undefined>) => {
      const projectId = action.payload
      if (!projectId) {
        state.selectedModelId = undefined
        state.selectedModelsByProject = {}
        localStorage.removeItem(SELECTED_MODELS_KEY)
        return
      }
      state.selectedModelId = undefined

      // Remove from project mapping
      const { [projectId]: removed, ...rest } = state.selectedModelsByProject
      state.selectedModelsByProject = rest
      localStorage.setItem(SELECTED_MODELS_KEY, JSON.stringify(rest))
    },

    // New action to handle project changes
    handleProjectChange: (state, action: PayloadAction<string>) => {
      const projectId = action.payload
      // Restore the selected model for this project if it exists
      state.selectedModelId = state.selectedModelsByProject[projectId]
    },

    setIsComparing: (state, action: PayloadAction<boolean>) => {
      state.isComparing = action.payload
      if (!action.payload) {
        state.comparingModelId = undefined
      }
    },

    setCompareModelId: (state, action: PayloadAction<string | undefined>) => {
      state.compareModelId = action.payload
      if (action.payload) {
        localStorage.setItem(COMPARE_MODEL_KEY, action.payload)
      } else {
        localStorage.removeItem(COMPARE_MODEL_KEY)
      }
    },

    clearCompareModelId: (state) => {
      state.compareModelId = undefined
      localStorage.removeItem(COMPARE_MODEL_KEY)
    },
  },
})

export const {
  setSelectedModelId,
  clearSelectedModelId,
  handleProjectChange,
  setIsComparing,
  setCompareModelId,
  clearCompareModelId,
} = modelSlice.actions

export const selectSelectedModelId = (state: RootState) => {
  const selectedProjectId = selectSelectedProjectId(state)
  if (!selectedProjectId) {
    // Fallback to legacy behavior when no project is selected
    return state.model.selectedModelId
  }
  // Return project-specific selected model
  return state.model.selectedModelsByProject[selectedProjectId]
}
export const selectSelectedModelIdForProject = (state: RootState, projectId: string) =>
  state.model.selectedModelsByProject[projectId]
export const selectIsComparing = (state: RootState) => state.model.isComparing
export const selectCompareModelId = (state: RootState) => state.model.compareModelId

export default modelSlice.reducer
